import ls from 'localstorage-slim';
import type { DemoConfig } from 'config/demo.config';
import type { BalanceResponse, EndRoundResponse, PlayResponse, Round } from 'stake-engine';
import type { AuthenticateData, RoundState } from '../../../utils/api.types';
import { winToMultiplier, winToPayout } from '../../../utils/win.utils';

export class DemoClient {
    #demo: DemoConfig;
    #state: AuthenticateData;
    #roundState: RoundState[] | null = null;

    constructor(demo: DemoConfig) {
        this.#demo = demo;
        this.#state = { ...demo.initState, round: this.openingRound() };

        // TODO: move to store
        const saved = ls.get<AuthenticateData>('demoState');
        if (saved) {
            // the save speaks for the wallet and the open round; what the game
            // offers comes from the config, so a save made before this game sold
            // a bonus buy still gets one
            this.#state = { ...saved, gameModes: demo.initState.gameModes };

            if (saved.round?.active) {
                // keep serving the same active round (same betID) after a refresh so
                // the real backend's "round stays open until EndRound" behaviour is
                // simulated and an in-flight free spins round can be resumed
                this.#roundState = saved.round.state as RoundState[];
            } else {
                // the saved round was settled, and a state with no round leaves the
                // reels on a random board. Re-open on the configured opening round
                // so the demo always starts from the same, known result.
                this.#state = { ...this.#state, round: this.openingRound() };
            }
        }
    }

    /**
     * The round the demo opens on, served straight out of the config. No spin was
     * ever placed for it, so it is staked at the default bet, and it is settled
     * here because the config only carries the book.
     */
    private openingRound(): Round | null {
        const round = this.#demo.initState.round;

        if (!round) return null;

        const states = (round.state ?? []) as RoundState[];
        const amount = round.amount ?? this.#demo.initState.config?.defaultBetLevel ?? 0;

        return { ...round, amount, ...this.settle(states, amount) };
    }

    async Authenticate(): Promise<AuthenticateData> {
        return this.state;
    }

    private generateRoundState(): RoundState[] {
        const { rounds, activeRound } = this.#demo;
        const round = rounds[activeRound];

        if (!round) {
            throw new Error(
                `Demo round "${activeRound}" is missing — known rounds: ${Object.keys(rounds).join(', ')}`,
            );
        }

        return round;
    }

    async Play(bet: { amount: number; mode: string }): Promise<PlayResponse> {
        this.state.balance.amount -= this.cost(bet.amount, bet.mode);

        if (!this.#roundState) {
            this.#roundState = this.generateRoundState();
        }

        const round: Round = {
            betID: Date.now(),
            amount: bet.amount,
            ...this.settle(this.#roundState, bet.amount),
            active: true,
            mode: bet.mode,
            state: this.#roundState,
        };

        // persist through the setter so a mid-round refresh restores this round
        this.state = { ...this.#state, round };

        return {
            balance: this.#state.balance,
            round,
        };
    }

    async EndRound(): Promise<EndRoundResponse> {
        // the RGS credits the round's payout on EndRound, not on Play — mirror
        // that here, otherwise a demo balance only ever goes down and a win
        // never lands on screen
        const balance = {
            ...this.#state.balance,
            amount: this.#state.balance.amount + (this.#state.round?.payout ?? 0),
        };

        this.#roundState = null;
        this.state = { ...this.#state, balance, round: null };

        return { balance };
    }

    /**
     * What a play in the given mode is charged. The RGS prices a bought feature at
     * the bet times the mode's cost multiplier, so a demo buy has to move the
     * balance by the same amount the popup offered it for. An unknown mode is
     * charged the plain bet — the base game's multiplier.
     */
    private cost(amount: number, mode: string): number {
        const gameMode = this.#state.gameModes?.find((offered) => offered.mode === mode);

        return amount * (gameMode?.costMultiplier ?? 1);
    }

    /**
     * What a book pays at a given bet. The RGS reports this on the round itself,
     * so the game never has to add a book up — the demo has to fill the same
     * fields in, or a demo round reads as paying nothing.
     */
    private settle(
        states: RoundState[],
        amount: number,
    ): Pick<Round, 'payout' | 'payoutMultiplier'> {
        const finalWin = states
            .filter((state): state is Extract<RoundState, { type: 'finalWin' }> => {
                return state.type === 'finalWin';
            })
            .at(-1);

        const win = Number(finalWin?.amount ?? 0);

        return { payout: winToPayout(win, amount), payoutMultiplier: winToMultiplier(win) };
    }

    async Balance(): Promise<BalanceResponse> {
        return { balance: this.#state.balance };
    }

    async Event(event: string) {
        console.log(`🧟 Demo event`, event);
    }

    get state(): AuthenticateData {
        return this.#state;
    }

    set state(state: AuthenticateData) {
        this.#state = state;

        ls.set('demoState', this.#state);
    }
}
